import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Fab, FabProps } from '@mui/material';

const DRAG_THRESHOLD_PX = 6;
const EDGE_PADDING_PX = 8;
const DEFAULT_FAB_SIZE_PX = 48;

type Position = { top: number; left: number };

type DefaultPosition =
  | { top: number; left: number }
  | { top: number; right: number };

interface DraggableMapFabProps extends Omit<FabProps, 'onClick'> {
  containerRef: React.RefObject<HTMLElement | null>;
  storageKey: string;
  defaultPosition: DefaultPosition;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

function loadPosition(storageKey: string): Position | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Position;
    if (typeof parsed.top === 'number' && typeof parsed.left === 'number') {
      return parsed;
    }
  } catch {
    // ignore invalid persisted value
  }
  return null;
}

function savePosition(storageKey: string, position: Position) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(position));
  } catch {
    // ignore quota errors
  }
}

function getDefaultLeft(defaultPosition: DefaultPosition, containerWidth: number, fabSize: number): number {
  if ('left' in defaultPosition) {
    return defaultPosition.left;
  }
  return containerWidth - fabSize - defaultPosition.right;
}

function clampPosition(position: Position, container: HTMLElement, fabSize: number): Position {
  const maxLeft = Math.max(EDGE_PADDING_PX, container.clientWidth - fabSize - EDGE_PADDING_PX);
  const maxTop = Math.max(EDGE_PADDING_PX, container.clientHeight - fabSize - EDGE_PADDING_PX);
  return {
    top: Math.max(EDGE_PADDING_PX, Math.min(position.top, maxTop)),
    left: Math.max(EDGE_PADDING_PX, Math.min(position.left, maxLeft)),
  };
}

function mergeRefs<T>(...refs: Array<React.Ref<T> | undefined>) {
  return (node: T | null) => {
    refs.forEach((ref) => {
      if (!ref) return;
      if (typeof ref === 'function') {
        ref(node);
      } else {
        (ref as React.MutableRefObject<T | null>).current = node;
      }
    });
  };
}

export const DraggableMapFab = React.forwardRef<HTMLButtonElement, DraggableMapFabProps>(function DraggableMapFab({
  containerRef,
  storageKey,
  defaultPosition,
  onClick,
  sx,
  ...fabProps
}, forwardedRef) {
  const fabRef = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState<Position>(() => {
    const saved = loadPosition(storageKey);
    if (saved) return saved;
    if ('left' in defaultPosition) {
      return { top: defaultPosition.top, left: defaultPosition.left };
    }
    return { top: defaultPosition.top, left: EDGE_PADDING_PX };
  });
  const [isDragging, setIsDragging] = useState(false);
  const dragStateRef = useRef({
    active: false,
    moved: false,
    startX: 0,
    startY: 0,
    originTop: 0,
    originLeft: 0,
    pointerId: -1,
  });

  const getFabSize = useCallback(() => {
    const width = fabRef.current?.getBoundingClientRect().width;
    return width && width > 0 ? width : DEFAULT_FAB_SIZE_PX;
  }, []);

  const syncPositionWithinContainer = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    setPosition((prev) => {
      const saved = loadPosition(storageKey);
      const base = saved ?? prev;
      const left =
        saved || 'left' in defaultPosition
          ? base.left
          : getDefaultLeft(defaultPosition, container.clientWidth, getFabSize());
      return clampPosition(
        { top: base.top, left },
        container,
        getFabSize(),
      );
    });
  }, [containerRef, defaultPosition, getFabSize, storageKey]);

  useEffect(() => {
    syncPositionWithinContainer();

    const container = containerRef.current;
    if (!container) return undefined;

    const observer = new ResizeObserver(syncPositionWithinContainer);
    observer.observe(container);
    return () => observer.disconnect();
  }, [containerRef, syncPositionWithinContainer]);

  const handlePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    dragStateRef.current = {
      active: true,
      moved: false,
      startX: event.clientX,
      startY: event.clientY,
      originTop: position.top,
      originLeft: position.left,
      pointerId: event.pointerId,
    };
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragStateRef.current;
    if (!drag.active || event.pointerId !== drag.pointerId) return;

    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;

    if (!drag.moved && (Math.abs(dx) > DRAG_THRESHOLD_PX || Math.abs(dy) > DRAG_THRESHOLD_PX)) {
      drag.moved = true;
      setIsDragging(true);
    }

    if (!drag.moved || !containerRef.current) return;

    const next = clampPosition(
      { top: drag.originTop + dy, left: drag.originLeft + dx },
      containerRef.current,
      getFabSize(),
    );
    setPosition(next);
  };

  const finishDrag = (event: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragStateRef.current;
    if (!drag.active || event.pointerId !== drag.pointerId) return;

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    if (drag.moved && containerRef.current) {
      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;
      const finalPosition = clampPosition(
        { top: drag.originTop + dy, left: drag.originLeft + dx },
        containerRef.current,
        getFabSize(),
      );
      setPosition(finalPosition);
      savePosition(storageKey, finalPosition);
    } else if (onClick) {
      onClick(event as unknown as React.MouseEvent<HTMLButtonElement>);
    }

    dragStateRef.current = { ...drag, active: false, moved: false, pointerId: -1 };
    setIsDragging(false);
  };

  return (
    <Fab
      ref={mergeRefs(fabRef, forwardedRef)}
      {...fabProps}
      onClick={(event) => {
        if (dragStateRef.current.moved) {
          event.preventDefault();
        }
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={finishDrag}
      onPointerCancel={finishDrag}
      sx={{
        position: 'absolute',
        top: position.top,
        left: position.left,
        zIndex: 10,
        touchAction: 'none',
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        ...sx,
      }}
    />
  );
});
